import type { RouteLocationNormalized, RouteLocationRaw } from "vue-router";

import type { RouteMeta } from "../types/types";

import { PERSISTENT_FILTER_PARAMS } from "@/shared/config/globalFilters.config";

type CarryLocation = Pick<RouteLocationNormalized, "path" | "query" | "meta">;

const isToolsRoute = (location: CarryLocation): boolean =>
  (location.meta as RouteMeta).workspace === "tools";

/**
 * Preserve filter query params when navigating between insights pages.
 * Only if navigating to a different path (not just query change), and never when either
 * side is in the Tools workspace: insights filters do not exist there. Error pages carry
 * no workspace and behave as before.
 * @returns the target location with the carried params, or null when nothing is carried
 */
export const carryFilterParams = (
  to: RouteLocationNormalized,
  from: CarryLocation,
): RouteLocationRaw | null => {
  if (isToolsRoute(from) || isToolsRoute(to)) return null;
  if (to.path === from.path || Object.keys(from.query).length === 0) return null;

  const persistedParams: Record<string, string> = {};

  for (const param of PERSISTENT_FILTER_PARAMS) {
    const value = from.query[param];
    if (value && !to.query[param]) {
      persistedParams[param] = Array.isArray(value) ? value[0] : value;
    }
  }

  // If we have params to persist and target doesn't have them
  if (Object.keys(persistedParams).length === 0) return null;

  return {
    ...to,
    query: {
      ...persistedParams,
      ...to.query,
    },
  };
};
